import React, { type FC, useContext, useEffect } from "react";
import { type RouteData, sc, type SitecoreContextValue, useSitecoreContext } from "~/foundation/Jss";
import { Helmet } from "react-helmet-async";
// import { VisitorIdentification } from "@sitecore-jss/sitecore-jss-react";
import { Fonts } from "~/foundation/Theme";
import { type EnablePageGraphicsBase, type HeaderBase, type MenuBase, type PageBase } from "~/foundation/Multisite/generated-types";
import { type PageDarkThemeBase, type PageThemeBase } from "~/foundation/Theme/generated-types";
import {
	getPageSpecificMetaTitle,
	getPageSpecificMetaDescription,
	getPageSpecificMetaImage,
	validateMetaDataString
} from "./getMetaData";
import { Favicon } from "./Head/Favicon";
import { ParallaxProvider } from 'react-scroll-parallax';
import { ColorThemeContext } from "~/foundation/Theme/ColorThemeContext";
import { type SuitcasePageBase } from "~/feature/Content/generated-types";
import { Suitcase } from "~/foundation/Suitcase";
import { ProgressBar } from "~/foundation/Components/ProgressBar";
import { PageGraphics } from "~/foundation/Components/PageGraphics";
import { useQueryString } from "~/foundation/Utils/useQueryString";
import { googleTagManagerIframe, googleTagManagerScriptTag, googleTagManagerDeniedStorageScriptTag, pushDataLayer } from "~/foundation/Tracking/GoogleTagManager";
import { pushEngageScriptTag } from "~/foundation/PushEngage/PushEngage";
import { useQualityVisitTracking } from "~/foundation/Tracking/useQualityVisitTracking";
import { getColorTheme } from "./AppRoot";
import { RenderSchema } from "./Schemas/RenderSchema";
import { SearchProvider } from "~/feature/Search/SearchProvider";
import { OneTrust, oneTrustHelmetScripts } from "~/foundation/OneTrust/OneTrust";
import { SkipNavContent } from "~/foundation/Chakra";
import { SkipToContent } from "~/feature/Navigation/SkipToContent/SkipToContent";
import { SlideInTagSearch } from "~/feature/Search/SlideInTagSearch";
import { AudioPlayer } from "~/feature/Article/AudioPlayer";
import { sitecoreContentConstants } from "~/foundation/SitecoreContent";
import { RestrictedAccess } from "~/foundation/RestrictedAccess";
import { StickyBadge } from "./StickyBadge";
import config from "~/temp/config";
import { type PushEngagePageBase } from "~/foundation/SitecoreContent/generated-types";
import { useLocation } from "react-router";
import { PDFViewerPage_TemplateId } from "./generated-types";
import { extractPdfFileNameFromMediaId } from "~/foundation/Utils/adobePdfViewMode";

/*
	APP LAYOUT
	This is where the app's HTML structure and root placeholders should be defined.

	All routes share this root layout by default (this could be customized in RouteHandler),
	but components added to inner placeholders are route-specific.
*/

type LayoutProps = {
	route: RouteData<PageBase & EnablePageGraphicsBase & MenuBase & PageThemeBase & SuitcasePageBase & PageDarkThemeBase & PushEngagePageBase>;
	isRtl: boolean;
}

const Layout: FC<LayoutProps> = ({ route, isRtl }) => {
	const { sitecoreContext } = useSitecoreContext<HeaderBase>();
	const location = useLocation();
	const queryString = useQueryString<{ disableThirdParty: "true" }>();
	const [currentColorTheme, setColorTheme] = useContext(ColorThemeContext);

	// Quality visit KPI tracking (time spent, scroll depth, multi-page)
	useQualityVisitTracking(route.itemId);

	const disableThirdParty = queryString.disableThirdParty === "true" || config.connected;

	const metaBrowserTitle = sitecoreContext.custom.settings.siteBrowserTitle;

	const metaTitle = validateMetaDataString(route.fields?.metaTitle?.value) || getPageSpecificMetaTitle(route) || validateMetaDataString(route.fields?.menuTitle?.value) || validateMetaDataString(route.fields?.pageTitle?.value) || route.name || "Saudi Aramco";
	const metaDesc = validateMetaDataString(route.fields?.metaDescription?.value) || getPageSpecificMetaDescription(route) || validateMetaDataString(route.fields?.metaTitle?.value) || validateMetaDataString(route.fields?.pageTitle?.value) || "";
	const metaImageObj = route.fields?.metaImage?.value?.src ? route.fields?.metaImage?.value : getPageSpecificMetaImage(route);

	const isPdfViewerPage = route.templateId === PDFViewerPage_TemplateId;
	const pdfFileNameTitle = isPdfViewerPage
		? extractPdfFileNameFromMediaId(new URLSearchParams(location.search).get("mediaId"))
		: null;
	const primaryPageTitle =
		pdfFileNameTitle ??
		(validateMetaDataString(route.fields?.pageTitle?.value) || metaTitle);
	const metaTitleForOpenGraph = pdfFileNameTitle ?? metaTitle;

	let metaImageArray: React.ReactNode[] = [];
	if (metaImageObj?.src) {
		metaImageArray = [
			<meta key={0} property="og:image" content={metaImageObj.src} />,
			<meta key={1} property="og:image:type" content="image/webp" />,
			<meta key={2} property="og:image:width" content={metaImageObj.width} />,
			<meta key={3} property="og:image:height" content={metaImageObj.height} />,
			<meta key={4} property="og:image:alt" content={metaImageObj.alt} />,
			<meta key={5} name="twitter:image" content={metaImageObj.src} />
		];
	}

	const navigationColorTheme = route?.fields?.navigationColorTheme?.value || route?.fields?.colorTheme?.value;
	const footerColorTheme = route?.fields?.footerColorTheme?.value || route?.fields?.colorTheme?.value;

	const googleAnalyticsId = sitecoreContext.custom.settings.googleAnalyticsId;
	const googleEnableConsentMode = sitecoreContext.custom.settings.googleEnableConsentMode;
	const googleGrantFunctionalityStorage = sitecoreContext.custom.settings.googleGrantFunctionalityStorage;

	const pushEngageAppId = sitecoreContext.custom.settings.pushEngageAppId;

	const pageEnablePushEngage = route.fields?.enablePushEngage?.value;

	const cookieConsent = sitecoreContext.custom.settings.cookieConsent;
	const useOneTrust = cookieConsent.useOneTrust && !sitecoreContext.pageEditing && !disableThirdParty;

	useEffect(() => {
		pushDataLayer(() => ({
			event: "page_view",
			page_type: route.templateName
		}));

		const colorTheme = getColorTheme(sitecoreContext, route);

		if (currentColorTheme.name !== colorTheme.name) {
			setColorTheme(colorTheme);
		}

	}, [route]);

	return (
		<ParallaxProvider>
			<Helmet
				htmlAttributes={{
					dir: isRtl ? "rtl" : "ltr",
					dataEditmode: sitecoreContext.pageEditing ? "inEditmode" : "",
					lang: sitecoreContext.language
				}}
				script={[
					// OneTrust must load before GTM/consent scripts; Helmet script attrs need a loose cast.
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					...(useOneTrust ? oneTrustHelmetScripts(cookieConsent.oneTrustID) as any[] : []),
					{
						innerHTML: `window.dataLayer = window.dataLayer || [];`
					},
					{
						innerHTML: googleTagManagerDeniedStorageScriptTag(googleGrantFunctionalityStorage, !googleEnableConsentMode || disableThirdParty)
					},
					{
						innerHTML: googleTagManagerScriptTag(googleAnalyticsId, disableThirdParty)
					}
					,
					{
						innerHTML: pushEngageScriptTag(pushEngageAppId, !pushEngageAppId || !pageEnablePushEngage || disableThirdParty)
					}
				]}
				noscript={[
					{
						innerHTML: googleTagManagerIframe(googleAnalyticsId, disableThirdParty)
					}
				]}
			>
				<title>
					{primaryPageTitle} {metaBrowserTitle}
				</title>

				<link rel="canonical" href={sitecoreContext.custom?.canonicalUrl} />
				<meta name="description" content={metaDesc} />
				{(sitecoreContext.route?.fields?.seoNoindex?.value || sitecoreContext.route?.fields?.seoNofollow?.value) && (
					<meta
						name="robots"
						content={[
							sitecoreContext.route?.fields?.seoNoindex?.value ? "noindex" : null,
							sitecoreContext.route?.fields?.seoNofollow?.value ? "nofollow" : null,
						]
							.filter(Boolean) // Remove nulls
							.join(",")} // Adds comma only if both exist
					/>
				)}
				{/*Facebook Meta Tags*/}
				<meta property="og:url" content={sitecoreContext.custom?.absoluteUrl} />
				<meta property="og:type" content="website" />
				<meta property="og:title" content={metaTitleForOpenGraph.toString()} />
				<meta property="og:description" content={metaDesc} />
				{metaImageArray}
				{/*Twitter Meta Tags*/}
				<meta name="twitter:title" content={metaTitleForOpenGraph.toString()} />
				<meta name="twitter:description" content={metaDesc} />
				<meta name="twitter:card" content="summary_large_image" />

				{sitecoreContext.custom.settings.isChinaSite &&
					<meta name="baidu-site-verification" content="codeva-LPWYBw7AnK" />
				}

				{sitecoreContext.custom?.hrefLangList.map((hrefModel, id) => (
					// eslint-disable-next-line
					// @ts-ignore
					<link key={id} rel="alternate" href={hrefModel.href} hreflang={hrefModel.hrefLang} /> // eslint-disable-line
				))}
			</Helmet>
			{useOneTrust &&
				<OneTrust sitecoreContext={sitecoreContext} />
			}
			<Favicon />
			<RenderSchema route={route} context={sitecoreContext} />
			{/*
				VisitorIdentification is necessary for Sitecore Analytics to determine if the visitor is a robot.
				If Sitecore XP (with xConnect/xDB) is used, this is required or else analytics will not be collected for the JSS app.
				For XM (CMS-only) apps, this should be removed.

				VI detection only runs once for a given analytics ID, so this is not a recurring operation once cookies are established.
				*/}
			{/*
			This is commented out until we know if they'll use Sitecore Tracking
			{!config.connected && (
				<VisitorIdentification />
			)}*/}
			<Fonts rtl={isRtl} siteLanguage={sitecoreContext.language} />
			{/* root placeholder for the app, which we add components to using route data */}
			<div>
				<SkipToContent />
				{/* Casting route to RouteData is only necessary here in the Layout file */}
				<sc.Placeholder name="header" rendering={route as RouteData} appendKey={navigationColorTheme} />
				<ProgressBar isRtl={isRtl} />
				<StickyBadge />
				<SkipNavContent />
				<main className="container">
					<SearchProvider active={sitecoreContext.custom.hasSearchFilter}>
						<sc.Placeholder name="page-top" rendering={route as RouteData} appendKey={route.itemId} />
						<sc.Placeholder name="page-layout" rendering={route as RouteData} appendKey={route.itemId} />
					</SearchProvider>
					<sc.Placeholder name="page-bottom" rendering={route as RouteData} appendKey={route.itemId} />
					{route.fields?.enablePageGraphics?.value && (
						<PageGraphics />
					)}
				</main>
				<sc.Placeholder name="footer" rendering={route as RouteData} appendKey={footerColorTheme} />
			</div>
			{route.fields?.disableSuitcase?.value === false && ( // has to be === false
				<Suitcase />
			)}
			{route.templateId === sitecoreContentConstants.templateIds.pageTypes.magazineArticleTemplateId &&
				<AudioPlayer sitecoreContext={sitecoreContext as SitecoreContextValue} />
			}
			{sitecoreContext.custom.settings.disclaimerInfo &&
				<RestrictedAccess />
			}
			<SlideInTagSearch />
			<sc.Placeholder name="page-script" rendering={route as RouteData} appendKey={route.itemId} />
		</ParallaxProvider>
	);
};

export default Layout;